import { useEffect, useState } from "react";
import { LayoutDashboard, Users, CreditCard, BarChart3, FileStack, LifeBuoy, Activity, GraduationCap, LibraryBig, TrendingUp, ShieldCheck, ScrollText, Loader2, DollarSign, Building2, Workflow, Bot, HardDrive, Megaphone, FileDown, LineChart, Sliders, Sparkles, Rocket } from "lucide-react";
import { PageHeader } from "@/pages/Dashboard";
import api from "@/lib/api";
import AdminExecutive from "@/pages/admin/AdminExecutive";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminIntelligence from "@/pages/admin/AdminIntelligence";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminWorkflows from "@/pages/admin/AdminWorkflows";
import AdminAI from "@/pages/admin/AdminAI";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminReports from "@/pages/admin/AdminReports";
import AdminInsights from "@/pages/admin/AdminInsights";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminContent from "@/pages/admin/AdminContent";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminAcademy from "@/pages/admin/AdminAcademy";
import AdminResources from "@/pages/admin/AdminResources";
import AdminProgress from "@/pages/admin/AdminProgress";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminTrust from "@/pages/admin/AdminTrust";
import AdminAcquisition from "@/pages/admin/AdminAcquisition";
import AdminCashflow from "@/pages/admin/AdminCashflow";
import AdminProfit from "@/pages/admin/AdminProfit";

const TABS = [
  { id: "overview", label: "Command Center", icon: LayoutDashboard, Comp: AdminExecutive, perm: "dashboard.view" },
  { id: "users", label: "Users", icon: Users, Comp: AdminUsers, perm: "users.view" },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard, Comp: AdminSubscriptions, perm: "subs.view" },
  { id: "plans", label: "Plans & Pricing", icon: Sliders, Comp: AdminPlans, perm: "subs.view" },
  { id: "intelligence", label: "Retention & BI", icon: LineChart, Comp: AdminIntelligence, perm: "dashboard.view" },
  { id: "acquisition", label: "Acquisition", icon: Rocket, Comp: AdminAcquisition, perm: "dashboard.view" },
  { id: "cashflow", label: "Cash Flow", icon: DollarSign, Comp: AdminCashflow, perm: "dashboard.view" },
  { id: "profit", label: "Profit & Scorecard", icon: TrendingUp, Comp: AdminProfit, perm: "dashboard.view" },
  { id: "trust", label: "Trust & Growth", icon: Sparkles, Comp: AdminTrust, perm: "dashboard.view" },
  { id: "finance", label: "Finance", icon: DollarSign, Comp: AdminFinance, perm: "finance.view" },
  { id: "businesses", label: "Businesses", icon: Building2, Comp: AdminBusinesses, perm: "business.view" },
  { id: "workflows", label: "Workflows", icon: Workflow, Comp: AdminWorkflows, perm: "workflow.view" },
  { id: "ai", label: "AI Management", icon: Bot, Comp: AdminAI, perm: "ai.view" },
  { id: "documents", label: "Documents", icon: HardDrive, Comp: AdminDocuments, perm: "content.manage" },
  { id: "analytics", label: "Analytics", icon: BarChart3, Comp: AdminAnalytics, perm: "dashboard.view" },
  { id: "insights", label: "Insights", icon: LineChart, Comp: AdminInsights, perm: "dashboard.view" },
  { id: "broadcast", label: "Broadcast", icon: Megaphone, Comp: AdminBroadcast, perm: "notifications.send" },
  { id: "reports", label: "Reports", icon: FileDown, Comp: AdminReports, perm: "reports.export" },
  { id: "progress", label: "Progress", icon: TrendingUp, Comp: AdminProgress, perm: "business.view" },
  { id: "academy", label: "Academy", icon: GraduationCap, Comp: AdminAcademy, perm: "content.manage" },
  { id: "resources", label: "Resources", icon: LibraryBig, Comp: AdminResources, perm: "content.manage" },
  { id: "content", label: "Content", icon: FileStack, Comp: AdminContent, perm: "content.manage" },
  { id: "tickets", label: "Support", icon: LifeBuoy, Comp: AdminTickets, perm: "support.manage" },
  { id: "roles", label: "Roles & Access", icon: ShieldCheck, Comp: AdminRoles, perm: "roles.manage" },
  { id: "audit", label: "Audit Logs", icon: ScrollText, Comp: AdminAudit, perm: "audit.view" },
  { id: "system", label: "System", icon: Activity, Comp: AdminSystem, perm: "system.view" },
];

export default function Admin() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => { api.get("/admin/me").then((r) => setMe(r.data)).catch(() => setMe({ permissions: [] })); }, []);

  if (!me) return <div className="flex justify-center py-24 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const perms = new Set(me.permissions || []);
  const visible = TABS.filter((t) => perms.has(t.perm));
  const activeTab = visible.find((t) => t.id === tab) || visible[0];
  const Active = activeTab?.Comp;

  return (
    <div className="fv-fade-up">
      <PageHeader eyebrow="Admin" title="Admin Command Center"
        subtitle={me.role_label ? `Signed in as ${me.role_label} — manage, monitor, and operate the entire platform.` : "Manage the platform."} />

      <div className="flex gap-1.5 mb-8 overflow-x-auto pb-1 -mx-1 px-1" data-testid="admin-tabs">
        {visible.map((t) => {
          const Icon = t.icon;
          const active = activeTab?.id === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                active ? "fv-gold-gradient text-[#0A0A0B] font-semibold" : "text-[#A1A1AA] hover:text-white hover:bg-white/5 border border-white/8"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {Active ? <Active /> : <p className="text-sm text-[#71717A]" data-testid="admin-no-access">Your admin role has no accessible sections.</p>}
    </div>
  );
}
